import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar } from '../components/Avatar';
import { type UserRole, useAuth, type User } from '../hooks/useAuth';
import { DialogHapus } from '../components/DialogHapus';
import { api, hapusTerkonfirmasi } from '../lib/api';
import { useBahasa } from '../i18n';

interface SwitchTarget {
  id: string;
  username: string;
  fullName: string;
}

interface ProfileResponse {
  user: User;
  impersonator: { id: string; username: string; fullName: string } | null;
  access: {
    role: UserRole;
    cameraCount: number;
    dashboards: { id: string; name: string }[];
  };
  switchTargets: SwitchTarget[];
}

const KARTU = 'rounded-lg border border-line bg-surface p-4';
const LABEL = 'flex flex-col gap-1 text-sm';
const INPUT =
  'min-h-tap rounded-md border border-line bg-surface px-3 text-sm disabled:cursor-not-allowed disabled:text-muted';
const TOMBOL_UTAMA =
  'min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.98] disabled:opacity-60';
const TOMBOL =
  'min-h-tap rounded-md border border-line px-3 text-sm transition-[background-color,scale] duration-150 hover:bg-subtle active:scale-[0.98] disabled:opacity-60';

/** Sama dengan batas server (§F7) — jangan sampai UI menjanjikan yang lebih longgar. */
const MIN_PASSWORD = 12;

/**
 * Halaman Profile (§F7).
 *
 * Empat kartu berdiri sendiri, masing-masing dengan tombol simpannya sendiri,
 * bukan satu form raksasa dengan satu tombol Save. Alasannya bukan estetika:
 * ganti password mencabut seluruh sesi dan pindah akun mengganti identitas
 * halaman ini — dua akibat yang tidak boleh ikut terpicu hanya karena seseorang
 * membetulkan ejaan nomor teleponnya.
 */
/** Peran seperti disebut orang (§F8) — sama dengan `AppHeader`. */
const LABEL_PERAN: Record<UserRole, string> = {
  admin: 'Administrator',
  operator: 'Operator',
  viewer: 'User',
};

export function ProfilePage() {
  const { t } = useBahasa();
  const { user, refresh } = useAuth();
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const muat = useCallback(async () => {
    try {
      setData(await api<ProfileResponse>('/me'));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void muat();
  }, [muat]);

  if (loading) return <p className="p-8 text-sm text-muted">Loading profile…</p>;
  if (!data || !user) {
    return (
      <p role="alert" className="p-8 text-sm text-amber-500">
        {error ?? t('Profile could not be loaded.')}
      </p>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="text-xl">{t('Profile')}</h1>

      {error && (
        <p role="alert" className="mt-4 rounded-md border border-red-600 px-3 py-2 text-sm">
          {error}
        </p>
      )}

      {/* Sesi pinjaman boleh melihat, tidak boleh mengubah (§F7.1). Kartu yang
          menulis dilepas seluruhnya, bukan diredupkan: form yang terlihat bisa
          diisi lalu ditolak server adalah cara paling pasti membuat orang
          mengira aplikasinya rusak. Yang tersisa justru yang dicari saat memakai
          fitur ini — hak akses orang yang sedang dilihat. */}
      {data.impersonator ? (
        <p className="mt-4 rounded-md border border-line bg-subtle px-3 py-2 text-sm">
          You are viewing {data.user.fullName}’s account. Switch back below to change your own
          details.
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-4">
        {!data.impersonator && (
          <>
            <KartuAvatar user={data.user} onSaved={() => void Promise.all([muat(), refresh()])} />
            <KartuIdentitas user={data.user} onSaved={() => void Promise.all([muat(), refresh()])} />
            <KartuPassword authSource={data.user.authSource} />
          </>
        )}
        <KartuAkses access={data.access} />
        <KartuPindahAkun
          impersonator={data.impersonator}
          targets={data.switchTargets}
          onSwitched={() => void refresh()}
        />
      </div>
    </div>
  );
}

/* ————————————————————————— Avatar ————————————————————————— */

function KartuAvatar({ user, onSaved }: { user: User; onSaved: () => void }) {
  const { t } = useBahasa();
  const berkasRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [pesan, setPesan] = useState<string | null>(null);
  const [akanHapusFoto, setAkanHapusFoto] = useState(false);

  async function unggah(file: File) {
    setBusy(true);
    setPesan(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(new Error(t('That file could not be read.')));
        fr.readAsDataURL(file);
      });

      await api('/me/avatar', { method: 'PUT', body: JSON.stringify({ image: dataUrl }) });
      // Penanda versinya datang dari server lewat `onSaved()` → `/auth/me`,
      // jadi tidak ada nonce yang perlu dipegang di sini. Itu juga yang membuat
      // avatar di sidebar ikut berubah pada saat yang sama.
      onSaved();
      setPesan(t('Photo updated.'));
    } catch (err) {
      setPesan((err as Error).message);
    } finally {
      setBusy(false);
      // Input direset supaya memilih berkas yang sama dua kali tetap memicu
      // `change` — kalau tidak, percobaan kedua setelah gagal terlihat diam.
      if (berkasRef.current) berkasRef.current.value = '';
    }
  }

  /**
   * Konfirmasinya sama dengan penghapusan lain (§G17), termasuk yang seringan
   * ini. Bukan karena fotonya berharga — ia bisa dipasang lagi dalam sepuluh
   * detik — melainkan karena satu tombol Delete yang cukup satu klik mengajari
   * orang bahwa Delete kadang tidak perlu dibaca, dan pelajaran itu terbawa ke
   * tombol yang bukan.
   */
  async function hapus(konfirmasi: Parameters<typeof hapusTerkonfirmasi>[1]) {
    await hapusTerkonfirmasi('/me/avatar', konfirmasi);
    setAkanHapusFoto(false);
    onSaved();
    setPesan(t('Photo removed.'));
  }

  return (
    <section className={KARTU}>
      <h2 className="text-base">{t('Photo')}</h2>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <Avatar
          userId={user.id}
          fullName={user.fullName}
          hasAvatar={user.hasAvatar}
          size="h-20 w-20 text-2xl"
          versi={user.avatarVersion}
        />

        <div className="flex flex-wrap items-center gap-2">
          {/* Input berkas asli disembunyikan dan dipicu tombol: kendali bawaan
              browser tidak bisa diberi gaya, dan teks "No file chosen" di
              sebelahnya tidak pernah benar setelah unggahan pertama. Ia tetap
              elemen sungguhan supaya papan ketik dan pembaca layar tetap dapat
              jalurnya lewat `<label>`. */}
          <input
            ref={berkasRef}
            id="avatar-file"
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void unggah(f);
            }}
          />
          <label htmlFor="avatar-file" className={`${TOMBOL} inline-flex cursor-pointer items-center`}>
            {user.hasAvatar ? t('Change photo') : t('Upload photo')}
          </label>

          {user.hasAvatar && (
            <button
              type="button"
              onClick={() => setAkanHapusFoto(true)}
              disabled={busy}
              className={TOMBOL}
            >
              {t('Remove')}
            </button>
          )}
        </div>
      </div>

      {akanHapusFoto && (
        <DialogHapus
          nama={user.username}
          judul={t('Remove your photo')}
          keterangan={t('Your initials take its place. A new photo can be uploaded any time.')}
          onBatal={() => setAkanHapusFoto(false)}
          onHapus={hapus}
        />
      )}

      <p className="mt-3 text-xs text-muted">
        {t('JPG or PNG. The image is cropped to a square and stored at 256×256.')}
      </p>
      {pesan && (
        <p role="status" className="mt-2 text-xs">
          {pesan}
        </p>
      )}
    </section>
  );
}

/* ———————————————————————— Identitas ———————————————————————— */

function KartuIdentitas({ user, onSaved }: { user: User; onSaved: () => void }) {
  const { t } = useBahasa();
  const [draft, setDraft] = useState({
    fullName: user.fullName,
    email: user.email ?? '',
    phone: user.phone ?? '',
    address: user.address ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [pesan, setPesan] = useState<string | null>(null);

  async function simpan(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setPesan(null);
    try {
      await api('/me', {
        method: 'PATCH',
        body: JSON.stringify({
          fullName: draft.fullName,
          email: draft.email.trim() === '' ? null : draft.email.trim(),
          phone: draft.phone.trim() === '' ? null : draft.phone.trim(),
          address: draft.address.trim() === '' ? null : draft.address.trim(),
        }),
      });
      onSaved();
      setPesan(t('Saved.'));
    } catch (err) {
      setPesan((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const set = (k: keyof typeof draft, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <section className={KARTU}>
      <h2 className="text-base">{t('Details')}</h2>

      <form onSubmit={(e) => void simpan(e)} className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className={LABEL}>
          <span className="text-muted">{t('Username')}</span>
          {/* Username tidak bisa diubah sendiri: ia identifier login dan dipakai
              di seluruh audit log. Ditampilkan tetap, bukan disembunyikan —
              orang butuh melihatnya untuk menyebutkannya saat minta bantuan. */}
          <input value={user.username} disabled className={INPUT} />
        </label>

        <label className={LABEL}>
          <span className="text-muted">{t('Full name')}</span>
          <input
            value={draft.fullName}
            onChange={(e) => set('fullName', e.target.value)}
            maxLength={128}
            required
            className={INPUT}
          />
        </label>

        <label className={LABEL}>
          <span className="text-muted">{t('Email')}</span>
          <input
            type="email"
            value={draft.email}
            onChange={(e) => set('email', e.target.value)}
            maxLength={128}
            className={INPUT}
          />
        </label>

        <label className={LABEL}>
          <span className="text-muted">{t('Phone')}</span>
          <input
            type="tel"
            value={draft.phone}
            onChange={(e) => set('phone', e.target.value)}
            maxLength={32}
            className={INPUT}
          />
        </label>

        <label className={`${LABEL} sm:col-span-2`}>
          <span className="text-muted">{t('Address')}</span>
          <input
            value={draft.address}
            onChange={(e) => set('address', e.target.value)}
            maxLength={255}
            className={INPUT}
          />
        </label>

        <div className="flex items-center gap-3 sm:col-span-2">
          <button type="submit" disabled={busy} className={TOMBOL_UTAMA}>
            {busy ? t('Saving…') : t('Save details')}
          </button>
          {pesan && (
            <span role="status" className="text-xs">
              {pesan}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

/* ———————————————————————— Password ———————————————————————— */

function KartuPassword({ authSource }: { authSource: 'local' | 'ldap' }) {
  const { t } = useBahasa();
  const ldap = authSource === 'ldap';
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [ulang, setUlang] = useState('');
  const [busy, setBusy] = useState(false);
  const [pesan, setPesan] = useState<string | null>(null);

  async function simpan(e: React.FormEvent) {
    e.preventDefault();
    if (next !== ulang) {
      setPesan(t('The two new passwords do not match.'));
      return;
    }
    setBusy(true);
    setPesan(null);
    try {
      await api('/me/password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      // Server mencabut semua sesi termasuk yang ini (§F4.2), jadi tidak ada
      // gunanya memperbarui state — muat ulang penuh mendaratkan orangnya di
      // halaman login, yang memang tujuannya.
      window.location.assign('/login');
    } catch (err) {
      setPesan((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <section className={KARTU}>
      <h2 className="text-base">{t('Password')}</h2>

      {ldap ? (
        <p className="mt-2 text-sm text-muted">
          {t('This account signs in with LDAP. Your password is managed in the directory, not here.')}
        </p>
      ) : (
        <form onSubmit={(e) => void simpan(e)} className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className={`${LABEL} sm:col-span-2`}>
            <span className="text-muted">{t('Current password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className={`${INPUT} sm:max-w-xs`}
            />
          </label>

          <label className={LABEL}>
            <span className="text-muted">{t('New password')}</span>
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={MIN_PASSWORD}
              required
              className={INPUT}
            />
          </label>

          <label className={LABEL}>
            <span className="text-muted">{t('Repeat new password')}</span>
            <input
              type="password"
              autoComplete="new-password"
              value={ulang}
              onChange={(e) => setUlang(e.target.value)}
              minLength={MIN_PASSWORD}
              required
              className={INPUT}
            />
          </label>

          <p className="text-xs text-muted sm:col-span-2">
            At least {MIN_PASSWORD} characters. Changing it signs you out everywhere.
          </p>

          <div className="flex items-center gap-3 sm:col-span-2">
            <button type="submit" disabled={busy} className={TOMBOL_UTAMA}>
              {busy ? t('Changing…') : t('Change password')}
            </button>
            {pesan && (
              <span role="alert" className="text-xs text-amber-500">
                {pesan}
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

/* ————————————————————————— Akses ————————————————————————— */

function KartuAkses({ access }: { access: ProfileResponse['access'] }) {
  const { t } = useBahasa();
  return (
    <section className={KARTU}>
      <h2 className="text-base">{t('Access')}</h2>

      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted">{t('Role')}</dt>
          {/* Label manusia, bukan nilai enum: yang tersimpan `viewer` demi 43
              akun hasil import, yang dibaca orang "User" (§F8). */}
          <dd className="text-sm">{t(LABEL_PERAN[access.role])}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">{t('Cameras you can view')}</dt>
          <dd className="text-sm">{access.cameraCount}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-xs text-muted">{t('Dashboards')}</dt>
          <dd className="mt-1 flex flex-wrap gap-1.5">
            {access.dashboards.length === 0 ? (
              <span className="text-sm text-muted">
                {access.role === 'admin' || access.role === 'operator'
                  ? t('All dashboards')
                  : t('None assigned yet')}
              </span>
            ) : (
              access.dashboards.map((d) => (
                <span key={d.id} className="rounded-full bg-subtle px-2.5 py-0.5 text-xs">
                  {d.name}
                </span>
              ))
            )}
          </dd>
        </div>
      </dl>

      <p className="mt-3 text-xs text-muted">{t('Ask an administrator to change any of this.')}</p>
    </section>
  );
}

/* ————————————————————— Pindah akun (§F7.1) ————————————————————— */

function KartuPindahAkun({
  impersonator,
  targets,
  onSwitched,
}: {
  impersonator: ProfileResponse['impersonator'];
  targets: SwitchTarget[];
  onSwitched: () => void;
}) {
  const { t } = useBahasa();
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [pesan, setPesan] = useState<string | null>(null);

  // Tidak sedang meminjam dan tidak punya akun tautan → kartunya tidak ada
  // gunanya sama sekali. Bagian kosong yang selalu tampil mengajari orang
  // melewatkan bagian itu.
  if (!impersonator && targets.length === 0) return null;

  async function masuk(id: string) {
    setBusy(true);
    setPesan(null);
    try {
      await api('/me/switch', { method: 'POST', body: JSON.stringify({ userId: id }) });
      // Muat ulang penuh, bukan sekadar `refresh()`: setiap halaman yang sedang
      // terbuka memegang daftar kamera dan dashboard milik akun sebelumnya, dan
      // menyegarkan identitasnya saja akan menyisakan layar berisi data yang
      // sudah tidak boleh dilihat akun yang baru.
      window.location.assign('/dashboard');
    } catch (err) {
      setPesan((err as Error).message);
      setBusy(false);
    }
  }

  async function kembali(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setPesan(null);
    try {
      await api('/me/switch-back', { method: 'POST', body: JSON.stringify({ password }) });
      onSwitched();
      window.location.assign('/dashboard');
    } catch (err) {
      setPesan((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <section className={KARTU}>
      <h2 className="text-base">{t('Switch account')}</h2>

      {impersonator && (
        <form onSubmit={(e) => void kembali(e)} className="mt-3">
          <p className="text-sm">
            {t('You are signed in as someone else. Enter the password for')}{' '}
            <strong>{impersonator.username}</strong> {t('to go back.')}
          </p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className={LABEL}>
              <span className="text-muted">{t('Password')}</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={INPUT}
              />
            </label>
            <button type="submit" disabled={busy} className={TOMBOL_UTAMA}>
              {t('Switch back')}
            </button>
          </div>
        </form>
      )}

      {targets.length > 0 && (
        <div className={impersonator ? 'mt-5 border-t border-line pt-4' : 'mt-3'}>
          <p className="text-sm text-muted">
            {t('Open one of these accounts to see exactly what they see. No password needed going in — coming back asks for yours.')}
          </p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {targets.map((akun) => (
              <li key={akun.id} className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm">
                  {akun.fullName} <span className="text-muted">· {akun.username}</span>
                </span>
                <button
                  type="button"
                  onClick={() => void masuk(akun.id)}
                  disabled={busy}
                  className={TOMBOL}
                >
                  {t('View as')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pesan && (
        <p role="alert" className="mt-2 text-xs text-amber-500">
          {pesan}
        </p>
      )}
    </section>
  );
}
